import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { fmtSignedUsd, colorForPnl } from "@/lib/format";

export const ClosePositionDialog = ({ open, onOpenChange, position, livePrices }) => {
  const counterKey = position?.counter_exchange?.toLowerCase();
  const [form, setForm] = useState({
    exit_price_darkex: "",
    exit_price_counter: "",
    funding_fees: 0,
    extra_fees: 0,
  });
  const qc = useQueryClient();

  const useLive = () => {
    setForm({
      ...form,
      exit_price_darkex: livePrices?.darkex?.price?.toFixed(2) || "",
      exit_price_counter: livePrices?.[counterKey]?.price?.toFixed(2) || "",
    });
  };

  const mutation = useMutation({
    mutationFn: (payload) => api.closePosition(position.id, payload),
    onSuccess: () => {
      toast.success("Pozisyon kapatıldı ve sonuçlandı");
      qc.invalidateQueries({ queryKey: ["positions"] });
      qc.invalidateQueries({ queryKey: ["stats"] });
      onOpenChange(false);
    },
    onError: (e) => {
      const detail = e?.response?.data?.detail || e?.message || e;
      toast.error(`Kapatma başarısız: ${detail}`);
    },
  });

  if (!position) return null;

  const preview = () => {
    const exD = parseFloat(form.exit_price_darkex);
    const exC = parseFloat(form.exit_price_counter);
    if (!exD || !exC) return null;
    const dPnl = (position.entry_price_darkex - exD) * position.size_eth;
    const cPnl = (exC - position.entry_price_counter) * position.size_eth;
    const bonus = dPnl < 0 ? Math.abs(dPnl) * position.bonus_rate : 0;
    const fees =
      position.entry_price_darkex * position.size_eth * position.darkex_fee_rate +
      position.entry_price_counter * position.size_eth * position.counter_fee_rate +
      exD * position.size_eth * position.darkex_fee_rate +
      exC * position.size_eth * position.counter_fee_rate +
      parseFloat(form.funding_fees || 0) +
      parseFloat(form.extra_fees || 0);
    const net = dPnl + cPnl + bonus - fees;
    return { dPnl, cPnl, bonus, fees, net };
  };

  const p = preview();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#121212] border-[#27272A] text-white max-w-xl rounded-md">
        <DialogHeader>
          <DialogTitle>Hedge Çiftini Kapat · {position.symbol}</DialogTitle>
          <p className="text-xs text-zinc-500">
            Karşı: {position.counter_exchange} · Miktar {position.size_eth} ETH
          </p>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Darkex Çıkış (short kapat)">
            <Input
              data-testid="close-exit-darkex"
              type="number"
              step="0.01"
              value={form.exit_price_darkex}
              onChange={(e) =>
                setForm({ ...form, exit_price_darkex: e.target.value })
              }
              className="bg-[#0A0A0A] border-[#FF3B30]/30 h-9 rounded-sm font-mono"
            />
          </Field>
          <Field label="Karşı Çıkış (long kapat)">
            <Input
              data-testid="close-exit-counter"
              type="number"
              step="0.01"
              value={form.exit_price_counter}
              onChange={(e) =>
                setForm({ ...form, exit_price_counter: e.target.value })
              }
              className="bg-[#0A0A0A] border-[#34C759]/30 h-9 rounded-sm font-mono"
            />
          </Field>
          <Field label="Funding Ücreti ($)">
            <Input
              data-testid="close-funding-fees"
              type="number"
              step="0.01"
              value={form.funding_fees}
              onChange={(e) => setForm({ ...form, funding_fees: e.target.value })}
              className="bg-[#0A0A0A] border-[#27272A] h-9 rounded-sm font-mono"
            />
          </Field>
          <Field label="Ek Ücretler ($)">
            <Input
              data-testid="close-extra-fees"
              type="number"
              step="0.01"
              value={form.extra_fees}
              onChange={(e) => setForm({ ...form, extra_fees: e.target.value })}
              className="bg-[#0A0A0A] border-[#27272A] h-9 rounded-sm font-mono"
            />
          </Field>
        </div>

        <Button
          variant="ghost"
          onClick={useLive}
          data-testid="close-use-live-btn"
          className="text-[#0A84FF] hover:bg-[#0A84FF]/10 rounded-sm justify-start h-8 px-2"
        >
          Canlı fiyatları kullan · Darkex ${livePrices?.darkex?.price?.toFixed(2) || "—"} ·{" "}
          {position.counter_exchange} $
          {livePrices?.[counterKey]?.price?.toFixed(2) || "—"}
        </Button>

        {p && (
          <div className="bg-[#0A0A0A] border border-[#27272A] rounded-sm p-3 grid grid-cols-2 gap-y-1.5 gap-x-4 text-xs font-mono">
            <Row label="Darkex K/Z (short)" value={p.dPnl} />
            <Row label="Karşı Borsa K/Z (long)" value={p.cPnl} />
            <Row label={`Bonus Kredisi (%${(position.bonus_rate * 100).toFixed(0)})`} value={p.bonus} amber />
            <Row label="Toplam Ücretler" value={-p.fees} />
            <div className="col-span-2 h-px bg-[#27272A] my-1" />
            <div className="col-span-2 flex justify-between items-baseline">
              <span className="text-[10px] uppercase tracking-wider text-zinc-400">
                Net Gerçekleşen K/Z
              </span>
              <span
                data-testid="close-preview-net"
                className={`text-lg font-bold ${colorForPnl(p.net)}`}
              >
                {fmtSignedUsd(p.net)}
              </span>
            </div>
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            className="text-zinc-400 hover:bg-[#1A1A1A] rounded-sm h-9"
          >
            Vazgeç
          </Button>
          <Button
            data-testid="confirm-close-btn"
            onClick={() =>
              mutation.mutate({
                exit_price_darkex: parseFloat(form.exit_price_darkex),
                exit_price_counter: parseFloat(form.exit_price_counter),
                funding_fees: parseFloat(form.funding_fees || 0),
                extra_fees: parseFloat(form.extra_fees || 0),
              })
            }
            disabled={
              mutation.isPending || !form.exit_price_darkex || !form.exit_price_counter
            }
            className="bg-[#34C759] hover:bg-[#28A745] text-black rounded-sm font-semibold h-9 px-4"
          >
            {mutation.isPending ? "Kapatılıyor…" : "Kapatmayı Onayla"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const Field = ({ label, children }) => (
  <div>
    <Label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">
      {label}
    </Label>
    {children}
  </div>
);

const Row = ({ label, value, amber }) => (
  <div className="flex justify-between">
    <span className="text-zinc-500">{label}</span>
    <span
      className={
        amber
          ? "text-[#FF9F0A]"
          : value > 0
          ? "text-[#34C759]"
          : value < 0
          ? "text-[#FF3B30]"
          : "text-zinc-300"
      }
    >
      {fmtSignedUsd(value)}
    </span>
  </div>
);
